import type {RefObject, SetStateAction} from 'react';
import {useCallback, useRef} from 'react';
import {useDispatch, useStore} from 'react-redux';
import {useApiStream} from '@Hooks/useApiStream';
import type {
  AiChatResumeParams,
  AiChatStreamParams,
  ChatMessage,
  ChatMessageModelImagePart,
  ChatMessagePart,
  ComponentStreamEvent,
  ControlStreamEvent,
  FailedMessageStreamEvent,
  SurveyStreamEvent,
  ImageStreamEvent,
  ProfileContext,
  ModelMessage,
  ModelMessageMetadataStreamEvent,
  ModeSpecificProps,
  ProgressStreamEvent,
  RawImageStreamEvent,
  RequestConfig,
  StreamDataChunk,
  TextStreamEvent,
} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMode, ChatType, ChatMessagePartType, MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import {createComponentPart} from '@Components/ai-chat-window/lib/stream-component-registry';
import {dispatchControlEvent} from '@Components/ai-chat-window/lib/control-event-handler';
import type {SurveySpec, ResumeWorkflowFn} from '@Components/ai-chat-window/components/survey/survey.types';
import {
  createStoppedErrorMessage,
  createThinkingMessage,
  deepCopyMessage,
  filterOutPlaceholderImageParts,
  handleStreamEvent,
  replaceThinkingOrAppendMessage,
  replaceThinkingOrUpdateMessage,
  updateMessageWithImageStream,
  updateMessageWithTextStream,
} from '@Components/ai-chat-window/lib/ai-chat-stream.library';
import {buildUserMessageReplacement, convertMessagesArrayToHashmap} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import {getUniqueString} from '@Utils/string.util';
import type {ChatState} from '@Components/ai-chat-window/ai-chat-window.reducer';
import {aiChatWindowActions} from '@Components/ai-chat-window/ai-chat-window.reducer';
import type {RootState} from '@/store';
import {useWakeLock} from '@Hooks/useWakeLock';
import {handleConnectionIssue} from '@Libraries/ai-chat.library';
import {useOpenChatImageInEditor} from '@Components/ai-chat-window/hooks/useOpenChatImageInEditor';
import {scrollToBottom} from '@Utils/dom.util';
import {omitKey} from '@Utils/record.util';
import type {HistoryItem} from '@AiChat/ai-chat.types';
import {AiAgentMode} from '@/ai-chat/providers/ai-chat-provider.types';

interface UseChatStreamProps {
  onChatStart?: () => void;
  setCurrentChatId?: (id: string) => void;
  refreshChatHistory?: () => void;
  refreshCredits?: () => void;
  onNewChat?: () => void;
  chatMessagesContainerRef: RefObject<HTMLDivElement | null>;
  onGenerationComplete?: (messages: Record<string, ChatMessage>) => void;
  modeSpecificProps: ModeSpecificProps;
  onUpdateHistoryItem?: (chatId: string, updates: Partial<HistoryItem>) => void;
  onSetSearchTags?: (chatId: string, tags: string[]) => void;
  onSetNextSuggestion?: (chatId: string, suggestion: string) => void;
  onEditMessageApplied?: (chatId: string) => void;
  endpoint?: string;
  mode?: AiAgentMode;
  contextProfile?: ProfileContext;
  onSurveyRequested?: (chatId: string, spec: SurveySpec, resumeToken: string) => void;
}

export interface UseChatStreamReturn {
  setMessages: (chatId: string, updater: SetStateAction<Record<string, ChatMessage>>) => void;
  setIsStreaming: (chatId: string, updater: SetStateAction<boolean>, currentIsStreaming: boolean) => void;
  setIsRefreshing: (chatId: string, isRefreshing: boolean) => void;
  setHasWriteAccess: (chatId: string, hasWriteAccess: boolean) => void;
  startGenerationStream: (
    chatId: string | undefined,
    userRequestParts: ChatMessagePart[],
    requestConfig?: RequestConfig,
    editOptions?: {editedMessageId: string; previousMessageId?: string}
  ) => Promise<void>;
  stopGeneration: (chatId: string) => Promise<void>;
  disconnect: () => void;
  resumeWorkflow: ResumeWorkflowFn;
  messageSessionUids: Map<string, string>;
  getChatState: (chatId: string) => ChatState;
}

export const useChatStream = ({
  onChatStart,
  setCurrentChatId,
  refreshChatHistory,
  refreshCredits,
  onNewChat,
  chatMessagesContainerRef,
  onGenerationComplete,
  modeSpecificProps,
  onUpdateHistoryItem,
  onSetSearchTags,
  onSetNextSuggestion,
  onEditMessageApplied,
  endpoint: endpointOverride,
  mode = AiAgentMode.CHAT,
  contextProfile,
  onSurveyRequested,
}: UseChatStreamProps): UseChatStreamReturn => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const {openPosterInEditor} = useOpenChatImageInEditor();
  const messageSessionUidsRef = useRef<Map<string, string>>(new Map());
  const refreshChatHistoryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const streamStartedCallbacksRef = useRef<Map<string, () => void>>(new Map());
  const pendingEditReplacementRef = useRef<{
    chatId: string;
    editedMessageId: string;
    parts: ChatMessagePart[];
  } | null>(null);

  const {lock: keepAwake, unlock: allowSleep} = useWakeLock();

  const debouncedRefreshChatHistory = useCallback(() => {
    if (refreshChatHistoryTimeoutRef.current) {
      clearTimeout(refreshChatHistoryTimeoutRef.current);
    }

    refreshChatHistoryTimeoutRef.current = setTimeout(() => {
      refreshChatHistory?.();
      refreshChatHistoryTimeoutRef.current = null;
    }, 300);
  }, [refreshChatHistory]);

  const getChatState = useCallback(
    (chatId: string | undefined): ChatState => {
      if (!chatId) {
        return {messages: {}, isStreaming: false, isRefreshing: false, error: null, hasWriteAccess: true, isDeleted: false, useSmartAssetSelection: null, source: null};
      }

      const state = store.getState();
      const chatState = state.aiChatWindow[chatId];
      return {
        messages: chatState?.messages ?? {},
        isStreaming: chatState?.isStreaming ?? false,
        isRefreshing: chatState?.isRefreshing ?? false,
        error: chatState?.error ?? null,
        hasWriteAccess: chatState?.hasWriteAccess ?? true,
        isDeleted: chatState?.isDeleted ?? false,
        useSmartAssetSelection: chatState?.useSmartAssetSelection ?? null,
        source: chatState?.source ?? null,
      };
    },
    [store]
  );

  const getCurrentMessages = useCallback(
    (chatId: string | undefined): Record<string, ChatMessage> => {
      if (!chatId) {
        return {};
      }

      const state = store.getState();
      return state.aiChatWindow[chatId]?.messages ?? {};
    },
    [store]
  );

  const setMessages = useCallback(
    (chatId: string | undefined, updater: SetStateAction<Record<string, ChatMessage>>) => {
      if (!chatId) {
        return;
      }

      const newMessages = typeof updater === 'function' ? updater(getCurrentMessages(chatId)) : updater;

      dispatch(aiChatWindowActions.setMessages({chatId, messages: newMessages}));
    },
    [dispatch, getCurrentMessages]
  );

  const setIsStreaming = (chatId: string | undefined, updater: SetStateAction<boolean>, currentIsStreaming: boolean): void => {
    if (!chatId) {
      return;
    }

    const value = typeof updater === 'function' ? updater(currentIsStreaming) : updater;

    dispatch(aiChatWindowActions.setIsStreaming({chatId, isStreaming: value}));
  };

  const setIsRefreshing = useCallback(
    (chatId: string | undefined, isRefreshing: boolean): void => {
      if (!chatId) {
        return;
      }

      dispatch(aiChatWindowActions.setIsRefreshing({chatId, isRefreshing: isRefreshing}));
    },
    [dispatch]
  );

  const setHasWriteAccess = useCallback(
    (chatId: string | undefined, hasWriteAccess: boolean): void => {
      if (!chatId) {
        return;
      }

      dispatch(aiChatWindowActions.setHasWriteAccess({chatId, hasWriteAccess}));
    },
    [dispatch]
  );

  const applyMessageEdit = useCallback(
    (chatId: string, editedMessageId: string, historyFromServer: ChatMessage[]): void => {
      const pending = pendingEditReplacementRef.current;
      pendingEditReplacementRef.current = null;

      const currentMessages = getCurrentMessages(chatId);
      let nextMessages = historyFromServer.length > 0 ? convertMessagesArrayToHashmap(historyFromServer) : {...currentMessages};

      if (pending?.chatId === chatId && pending.editedMessageId === editedMessageId && pending.parts.length > 0) {
        nextMessages = {
          ...nextMessages,
          [editedMessageId]: buildUserMessageReplacement(editedMessageId, pending.parts),
        };
      }

      // Preserve any assistant/thinking messages that streaming already added
      // before this control event arrived (race condition).
      for (const [key, msg] of Object.entries(currentMessages)) {
        if (!(key in nextMessages) && msg.role === MessageRole.ASSISTANT) {
          nextMessages[key] = msg;
        }
      }

      setMessages(chatId, nextMessages);
      onEditMessageApplied?.(chatId);
    },
    [setMessages, onEditMessageApplied, getCurrentMessages]
  );

  const handleControlEvent = useCallback(
    (event: ControlStreamEvent): void => {
      dispatchControlEvent(event, {
        debouncedRefreshChatHistory,
        onNewChat,
        onUpdateHistoryItem,
        onSetSearchTags,
        onSetNextSuggestion,
        getCurrentMessages,
        setMessages,
        refreshCredits,
        applyMessageEdit,
        openPosterInEditor,
      });
    },
    [applyMessageEdit, getCurrentMessages, setMessages, debouncedRefreshChatHistory, refreshCredits, onUpdateHistoryItem, onSetSearchTags, onSetNextSuggestion, openPosterInEditor, onNewChat]
  );

  const handleProgressEvent = useCallback(
    (event: ProgressStreamEvent): void => {
      const chatId = event.idAiChat;
      const currentMessages = getCurrentMessages(chatId);
      const thinkingMessage = createThinkingMessage(event.data);
      const newMessages = replaceThinkingOrAppendMessage(currentMessages, thinkingMessage);

      setMessages(chatId, newMessages);
    },
    [getCurrentMessages, setMessages]
  );

  const handleTextStreamEvent = useCallback(
    (event: TextStreamEvent): void => {
      const chatId = event.idAiChat;
      const currentMessages = getCurrentMessages(chatId);
      const newMessages = replaceThinkingOrUpdateMessage(currentMessages, event.message, (existing) => updateMessageWithTextStream(existing, event));
      setMessages(chatId, newMessages);
    },
    [getCurrentMessages, setMessages]
  );

  const markNewImagePartsForDisplay = (message: ChatMessage, showFirstImageGenerationDelight: boolean): void => {
    message.parts.forEach((part) => {
      if (part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE) {
        part.isNewGeneration = true;
        part.showFirstImageGenerationDelight = showFirstImageGenerationDelight;
      }
    });
  };

  const handleImageStreamEvent = useCallback(
    (event: ImageStreamEvent): void => {
      markNewImagePartsForDisplay(event.message, event.showFirstImageGenerationDelight);

      const chatId = event.idAiChat;
      const currentMessages = getCurrentMessages(chatId);
      const newMessages = replaceThinkingOrUpdateMessage(currentMessages, event.message, (existing) => updateMessageWithImageStream(existing, event));

      setMessages(chatId, newMessages);

      if (event.useSmartAssetSelection !== undefined) {
        dispatch(aiChatWindowActions.setUseSmartAssetSelection({chatId, useSmartAssetSelection: event.useSmartAssetSelection}));
      }

      onGenerationComplete?.(newMessages);
    },
    [getCurrentMessages, setMessages, onGenerationComplete]
  );

  const handleRawImageStreamEvent = useCallback(
    (event: RawImageStreamEvent): void => {
      const chatId = event.idAiChat;
      const currentMessages = getCurrentMessages(chatId);

      const parts: ChatMessagePart[] = [];

      if (event.textContent) {
        parts.push({
          type: ChatMessagePartType.TEXT,
          text: event.textContent,
          provider: event.provider,
        });
      }

      const rawImagePart: ChatMessageModelImagePart = {
        type: ChatMessagePartType.MODEL_GENERATED_IMAGE,
        hashedId: '',
        width: null,
        height: null,
        base64ImageData: event.base64ImageData,
        isRawBlurred: true,
        isNewGeneration: true,
        provider: event.provider,
      };

      parts.push(rawImagePart);

      const rawImageMessage: ModelMessage = {
        role: MessageRole.ASSISTANT,
        isThinking: false,
        parts: parts,
        messageId: getUniqueString(),
        feedback: null,
        usage: {inputTokens: 0, outputTokens: 0},
      };

      const newMessages = replaceThinkingOrUpdateMessage(currentMessages, rawImageMessage, (existing) => {
        const updatedMessage = deepCopyMessage(existing) as ModelMessage;
        const existingParts = updatedMessage.parts ?? [];
        const partsWithoutPlaceholders = filterOutPlaceholderImageParts(existingParts);
        const hasExistingText = partsWithoutPlaceholders.some((part) => part.type === ChatMessagePartType.TEXT);
        const partsToAdd = hasExistingText ? [rawImagePart] : parts;
        updatedMessage.parts = [...partsWithoutPlaceholders, ...partsToAdd];
        return updatedMessage;
      });

      setMessages(chatId, newMessages);
    },
    [getCurrentMessages, setMessages]
  );

  const handleFailedMessageStreamEvent = useCallback(
    (event: FailedMessageStreamEvent): void => {
      const chatId = event.idAiChat;
      setIsStreaming(chatId, false, true);
      const currentMessages = getCurrentMessages(chatId);
      const newMessages = replaceThinkingOrAppendMessage(currentMessages, event.message);
      setMessages(chatId, newMessages);

      refreshCredits?.();
    },
    [setIsStreaming, setMessages, refreshCredits, getCurrentMessages]
  );

  const handleModelMessageMetadataEvent = useCallback(
    (event: ModelMessageMetadataStreamEvent): void => {
      const chatId = event.idAiChat;
      const currentMessages = getCurrentMessages(chatId);
      const targetMessage = currentMessages[event.messageId];

      if (targetMessage?.role !== MessageRole.ASSISTANT) {
        return;
      }

      const updatedMessage: ModelMessage = {
        ...targetMessage,
        enhancedPrompt: event.enhancedPrompt,
        context: event.context,
      };

      setMessages(chatId, {...currentMessages, [event.messageId]: updatedMessage});
      dispatch(aiChatWindowActions.setUseSmartAssetSelection({chatId, useSmartAssetSelection: event.useSmartAssetSelection}));
    },
    [dispatch, getCurrentMessages, setMessages]
  );

  const handleSurveyEvent = useCallback(
    (event: SurveyStreamEvent): void => {
      const chatId = event.idAiChat;
      setIsStreaming(chatId, false, true);
      onSurveyRequested?.(chatId, event.spec, event.resumeToken);
    },
    [setIsStreaming, onSurveyRequested]
  );

  const handleComponentEvent = useCallback(
    (event: ComponentStreamEvent): void => {
      const componentPart = createComponentPart(event);
      if (!componentPart) {
        return;
      }

      const chatId = event.idAiChat;
      const currentMessages = getCurrentMessages(chatId);

      const componentMessage: ModelMessage = {
        role: MessageRole.ASSISTANT,
        isThinking: false,
        parts: [componentPart],
        messageId: event.messageId,
        feedback: null,
        usage: {inputTokens: 0, outputTokens: 0},
      };

      const newMessages = replaceThinkingOrAppendMessage(currentMessages, componentMessage);
      setMessages(chatId, newMessages);
    },
    [getCurrentMessages, setMessages]
  );

  const {connect, disconnect} = useApiStream<StreamDataChunk, AiChatStreamParams | AiChatResumeParams>({
    onMessage: (streamEvent) => {
      const chatId = (streamEvent as {idAiChat?: string}).idAiChat;
      if (chatId) {
        const onStreamStarted = streamStartedCallbacksRef.current.get(chatId);
        if (onStreamStarted) {
          onStreamStarted();
          streamStartedCallbacksRef.current.delete(chatId);
        }
      }
      handleStreamEvent(streamEvent, {
        onControlEvent: handleControlEvent,
        onProgressEvent: handleProgressEvent,
        onTextStreamEvent: handleTextStreamEvent,
        onImageStreamEvent: handleImageStreamEvent,
        onRawImageStreamEvent: handleRawImageStreamEvent,
        onFailedMessageEvent: handleFailedMessageStreamEvent,
        onModelMessageMetadataEvent: handleModelMessageMetadataEvent,
        onSurveyEvent: handleSurveyEvent,
        onComponentEvent: handleComponentEvent,
      });
    },

    onClose: (chatId: string) => {
      if (!chatId) {
        return;
      }

      if (pendingEditReplacementRef.current?.chatId === chatId) {
        pendingEditReplacementRef.current = null;
      }

      debouncedRefreshChatHistory();

      const currentMessages: Record<string, ChatMessage> = getCurrentMessages(chatId);

      const newMessages = Object.entries(currentMessages).reduce((acc: Record<string, ChatMessage>, [key, msg]: [string, ChatMessage]) => {
        const isThinkingAssistantMessage = msg.role === MessageRole.ASSISTANT && 'isThinking' in msg && (msg as {isThinking: boolean}).isThinking;

        if (!isThinkingAssistantMessage) {
          acc[key] = msg;
        }

        return acc;
      }, {});

      setMessages(chatId, newMessages);

      setIsStreaming(chatId, false, true);

      void allowSleep();
    },

    onError: (e: unknown, chatId: string) => {
      if (!chatId) {
        return;
      }

      if (pendingEditReplacementRef.current?.chatId === chatId) {
        pendingEditReplacementRef.current = null;
      }

      const onStreamStarted = streamStartedCallbacksRef.current.get(chatId);
      if (onStreamStarted) {
        onStreamStarted();
        streamStartedCallbacksRef.current.delete(chatId);
      }

      dispatch(aiChatWindowActions.setError({chatId: chatId, error: window.i18next.t('pmwjs_ai_chat_stream_error')}));

      if (typeof e !== 'object' || e === null || !('status' in e) || !('message' in e)) {
        return;
      }

      const isNetworkError = (e as {status: unknown}).status === 0 || (e as {message: unknown}).message === 'Failed to fetch';

      if (isNetworkError) {
        handleConnectionIssue();
      }
    },
  });

  const setupStatesForNewMessage = (chatId: string, userMessageParts: ChatMessagePart[]): void => {
    const isEditFlow = pendingEditReplacementRef.current?.chatId === chatId;

    if (isEditFlow && pendingEditReplacementRef.current) {
      const editedMessageId = pendingEditReplacementRef.current.editedMessageId;
      const pendingParts = pendingEditReplacementRef.current.parts;
      setMessages(chatId, (prev) => {
        const messageEntries = Object.entries(prev);
        const editedMessageIndex = messageEntries.findIndex(([key]) => key === editedMessageId);

        if (editedMessageIndex !== -1) {
          // Keep messages up to and including the edited message
          const entriesToKeep = messageEntries.slice(0, editedMessageIndex + 1);
          if (pendingParts && pendingParts.length > 0) {
            const originalMessageKey = entriesToKeep[editedMessageIndex][0];
            const originalMessage = entriesToKeep[editedMessageIndex][1];
            entriesToKeep[editedMessageIndex] = [originalMessageKey, {...originalMessage, parts: pendingParts}];
          }
          return Object.fromEntries(entriesToKeep);
        }
        return prev;
      });
    } else if (!isEditFlow) {
      const newMessageId = getUniqueString();
      setMessages(chatId, (prev) => ({
        ...prev,
        [newMessageId]: buildUserMessageReplacement(newMessageId, userMessageParts),
      }));
    }

    setCurrentChatId?.(chatId);
    setIsStreaming(chatId, true, true);

    void keepAwake();
  };

  const initiateLoading = (chatId: string, loadingTexts?: string[]): void => {
    const initialThinkingMessage = createThinkingMessage(loadingTexts && loadingTexts.length > 0 ? loadingTexts : ['']);
    const messageId = initialThinkingMessage.messageId;
    if (messageId) {
      setMessages(chatId, (prev) => ({
        ...prev,
        [messageId]: initialThinkingMessage,
      }));
    }
  };

  const triggerChatStartIfNeeded = (chatId?: string): void => {
    const currentMessages = getCurrentMessages(chatId);

    if (Object.values(currentMessages).length === 0) {
      onChatStart?.();
    }
  };

  const startGenerationStream = useCallback(
    async (
      chatId: string | undefined,
      userRequestParts: ChatMessagePart[],
      requestConfig?: RequestConfig,
      editOptions?: {editedMessageId: string; previousMessageId?: string}
    ): Promise<void> => {
      triggerChatStartIfNeeded(chatId);

      const chatIdToUse = chatId ?? getUniqueString();

      if (editOptions?.editedMessageId) {
        pendingEditReplacementRef.current = {
          chatId: chatIdToUse,
          editedMessageId: editOptions.editedMessageId,
          parts: userRequestParts,
        };
      }

      setupStatesForNewMessage(chatIdToUse, userRequestParts);
      initiateLoading(chatIdToUse, requestConfig?.loadingTexts);

      const messageSessionUid = getUniqueString();
      messageSessionUidsRef.current.set(chatIdToUse, messageSessionUid);
      const endpoint = endpointOverride ?? (mode === AiAgentMode.AGENT ? 'marketingagent/messages' : 'ai/messages');
      setTimeout(() => {
        scrollToBottom(chatMessagesContainerRef.current);
      }, 0);

      try {
        const params: AiChatStreamParams = {
          chatId: chatIdToUse,
          type: ChatType.DESIGN,
          userRequestParts,
          messageSessionUid,
          ...(editOptions?.editedMessageId
            ? {mode: ChatMode.EDIT_MESSAGE, editedMessageId: editOptions.editedMessageId, previousMessageId: editOptions.previousMessageId}
            : modeSpecificProps),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        if (contextProfile) {
          params.contextProfile = contextProfile;
        }

        if (requestConfig) {
          params.requestConfig = requestConfig;
        }

        await connect(endpoint, params, chatIdToUse);
      } catch  {
        if (chatIdToUse) {
          if (pendingEditReplacementRef.current?.chatId === chatIdToUse) {
            pendingEditReplacementRef.current = null;
          }
          streamStartedCallbacksRef.current.get(chatIdToUse)?.();
          streamStartedCallbacksRef.current.delete(chatIdToUse);
          dispatch(aiChatWindowActions.setError({chatId: chatIdToUse, error: window.i18next.t('pmwjs_ai_chat_stream_start_error')}));
          setIsStreaming(chatIdToUse, false, false);
          messageSessionUidsRef.current.delete(chatIdToUse);
        }
      } finally {
        void allowSleep();
      }
    },
    [connect, onChatStart, setIsStreaming, dispatch, getCurrentMessages, modeSpecificProps, contextProfile]
  );

  const pruneImagesFromLastMessageAndUpdateState = (chatId: string, message: ChatMessage | null, isStreaming: boolean): ChatMessage | null => {
    const shouldProcess = isStreaming && message?.role === MessageRole.ASSISTANT && !message?.isThinking;

    if (!shouldProcess || !message?.messageId) {
      return null;
    }

    const nonImageParts = (message.parts ?? []).filter((part) => part.type !== ChatMessagePartType.MODEL_GENERATED_IMAGE);

    const currentMessages = getCurrentMessages(chatId);

    let finalMessage: ChatMessage | null;
    let newMessages: Record<string, ChatMessage>;

    if (nonImageParts.length === 0) {
      newMessages = omitKey(currentMessages, message.messageId);
      finalMessage = null;
    } else {
      finalMessage = {
        ...message,
        parts: nonImageParts,
      };
      newMessages = {
        ...currentMessages,
        [message.messageId]: finalMessage,
      };
    }

    setMessages(chatId, newMessages);

    return finalMessage;
  };

  const stopGeneration = useCallback(
    async (chatId: string): Promise<void> => {
      disconnect(chatId);

      const {messages: currentMessages, isStreaming: currentIsStreaming} = getChatState(chatId);

      const messageEntries = Object.entries(currentMessages);
      const lastMessageEntry = messageEntries.length > 0 ? messageEntries[messageEntries.length - 1] : null;
      const lastMessage = lastMessageEntry ? lastMessageEntry[1] : null;

      const currentStreamContent = pruneImagesFromLastMessageAndUpdateState(chatId, lastMessage, currentIsStreaming);

      try {
        const errorMessage = createStoppedErrorMessage();

        const currentMessagesAfterPrune = getCurrentMessages(chatId);

        const newMessages = replaceThinkingOrAppendMessage(currentMessagesAfterPrune, errorMessage);
        setMessages(chatId, newMessages);
        setIsStreaming(chatId, false, false);
        const uidToStop = messageSessionUidsRef.current.get(chatId);

        await window.PMW.writeLocal('ai/stopChat', {
          chatId: chatId,
          currentStreamContent: currentStreamContent ?? [],
          messageSessionUid: uidToStop,
        });

        messageSessionUidsRef.current.delete(chatId);

        void allowSleep();
      } catch (e: unknown) {
        console.error('Failed to stop generation. Details:', e);
      }
    },
    [disconnect, getCurrentMessages, setMessages, getChatState, allowSleep]
  );

  const resumeWorkflow = useCallback<ResumeWorkflowFn>(
    async (chatId, params, userEchoText) => {
      const messageSessionUid = params.messageSessionUid;
      if (typeof messageSessionUid === 'string') {
        messageSessionUidsRef.current.set(chatId, messageSessionUid);
      }

      const echoParts: ChatMessagePart[] = [];
      if (userEchoText) {
        echoParts.push({type: ChatMessagePartType.TEXT, text: userEchoText});
      }
      if (params.userRequestParts) {
        echoParts.push(...params.userRequestParts);
      }
      if (echoParts.length > 0) {
        const echoMessageId = getUniqueString();
        setMessages(chatId, (prev) => ({
          ...prev,
          [echoMessageId]: buildUserMessageReplacement(echoMessageId, echoParts),
        }));
      }

      setIsStreaming(chatId, true, true);
      initiateLoading(chatId);
      void keepAwake();

      if (mode !== AiAgentMode.AGENT) {
        setIsStreaming(chatId, false, false);
        void allowSleep();
        return;
      }

      try {
        await connect('marketingagent/messages', params, chatId);
      } catch (e) {
        setIsStreaming(chatId, false, false);
        dispatch(aiChatWindowActions.setError({chatId, error: window.i18next.t('pmwjs_ai_chat_stream_start_error')}));
        throw e;
      } finally {
        void allowSleep();
      }
    },
    [connect, setIsStreaming, setMessages, keepAwake, allowSleep, dispatch]
  );

  return {
    setMessages,
    setIsStreaming,
    setIsRefreshing,
    setHasWriteAccess,
    startGenerationStream,
    stopGeneration,
    disconnect,
    resumeWorkflow,
    messageSessionUids: messageSessionUidsRef.current,
    getChatState,
  };
};
